import { NextSeoProps } from "next-seo";
import { match } from "ts-pattern";

import { Enum_Componententriessmartphoneentry_Category } from "graphql.d";
import { ProductSectionData } from "shared/product/ProductSection/product-section.helper";
import { applyCDNToUrl } from "ui/StrapiImage";
import { config } from "utils/config.utils";
import { formatToCurrency, fromCents } from "utils/price.utils";
import { SEOProperties } from "utils/strapi/response";
import { initializeApollo } from "utils/with-apollo.utils";

import {
  RatingsAverageDocument,
  RatingsAverageQuery,
} from "./product-average-rating.query.generated";

const CatalogCategory = Enum_Componententriessmartphoneentry_Category;

export const getProductMinimumPrice = ({ variants }: ProductSectionData) =>
  Math.min(
    ...(variants?.map(({ subscriptionPrice }) => subscriptionPrice) ?? []),
  );

export const getProductMinimumPriceFormatted = (
  productData: ProductSectionData,
) => `${formatToCurrency("fr-FR", "EUR", getProductMinimumPrice(productData))}`;

export const getProductTitle = (productData: ProductSectionData) => {
  const {
    family: { category, modelName },
  } = productData;

  return category === "available"
    ? `Location ${modelName} à ${getProductMinimumPriceFormatted(
        productData,
      )}/mois`
    : `Location ${modelName}`;
};

export const getProductDescription = (productData: ProductSectionData) => {
  const {
    family: { category, brand, modelName },
  } = productData;

  return [
    "Louez",
    brand === "Apple" ? null : "le Samsung",
    brand === "Apple" ? `l'${modelName}` : `${modelName}`,
    category === "available"
      ? `dès ${getProductMinimumPriceFormatted(productData)}/mois`
      : null,
    `garantie casse et vol incluse. 98% de clients satisfaits. En stock, livraison en 24 heures !`,
  ]
    .filter(Boolean)
    .join(" ");
};

export const getProductAverageRating = async () => {
  const apolloClient = initializeApollo();

  const {
    data: { result },
  } = await apolloClient.query<RatingsAverageQuery>({
    query: RatingsAverageDocument,
  });

  const count = result?.count || 2600;
  const avg = (result?.average || 4.4).toFixed(1);

  return { count, avg };
};

export const getProductPageSeo = async (
  productSectionData: ProductSectionData,
  referenceSlug: string,
  familySlug: string,
): Promise<Partial<SEOProperties & NextSeoProps>> => {
  const { variants, family } = productSectionData;
  const currentVariant = variants?.find(
    ({ variantSlug }) => variantSlug === referenceSlug,
  );

  const title = getProductTitle(productSectionData);
  const minimumPrice = getProductMinimumPrice(productSectionData);
  const description = getProductDescription(productSectionData);
  const image = applyCDNToUrl(family.ogImageUrl);

  const defaultAdditionalMetaTags = [
    {
      property: "image",
      content: image ?? "",
    },
    {
      property: "product:brand",
      content: family.brand ?? "",
    },
    {
      property: "product:retailer_item_id",
      content: currentVariant?.sku ?? "",
    },
  ];

  const categoryRelatedMetaTags = match(family.category)
    .with(CatalogCategory.Available, () => [
      {
        property: "product:availability",
        content: currentVariant?.isInStock ? "in stock" : "out of stock",
      },
      {
        property: "product:price:currency",
        content: "EUR",
      },
      {
        property: "product:price:amount",
        content: fromCents(minimumPrice).toString(),
      },
    ])
    .with(CatalogCategory.ComingSoon, () => [
      {
        property: "product:availability",
        content: "preorder",
      },
    ])
    .with(CatalogCategory.Legacy, () => [
      {
        property: "product:availability",
        content: "discontinued",
      },
    ])
    .otherwise(() => []);

  const openGraph = {
    title,
    description,
    images: [
      {
        url: image ?? "",
        alt: title,
        height: 628,
        width: 1200,
      },
    ],
    site_name: "Mobile Club",
    locale: "fr_FR",
    type: "product",
  };

  return {
    title,
    description,
    canonical: `${config.website_url}/smartphone/${familySlug}`,
    titleTemplate: "%s - MobileClub",
    additionalMetaTags: [
      ...defaultAdditionalMetaTags,
      ...categoryRelatedMetaTags,
    ],
    openGraph,
  };
};
